/* eslint-disable max-len */
import moment from 'moment';

// Date Presets
export const TODAY = 'today';
export const YESTERDAY = 'yesterday';
export const THIS_MONTH = 'this_month';
export const LAST_MONTH = 'last_month';
export const THIS_QUARTER = 'this_quarter';
export const LAST_QUARTER = 'last_quarter';
export const THIS_YEAR = 'this_year';
export const LAST_YEAR = 'last_year';
export const MAXIMUM = 'maximum';
export const LAST_3D = 'last_3d';
export const LAST_7D = 'last_7d';
export const LAST_14D = 'last_14d';
export const LAST_28D = 'last_28d';
export const LAST_30D = 'last_30d';
export const LAST_90D = 'last_90d';
export const LAST_WEEK_MON_SUN = 'last_week_mon_sun';
export const LAST_WEEK_SUN_SAT = 'last_week_sun_sat';
export const THIS_WEEK_MON_TODAY = 'this_week_mon_today';
export const THIS_WEEK_SUN_TODAY = 'this_week_sun_today';

// Aggregate Periods
export const DAY = 'day';
export const WEEK = 'week';
export const DAYS_28 = 'days_28';
export const LIFETIME = 'lifetime';

// Metrics
export const AUDIENCE_CITY = 'audience_city';
export const AUDIENCE_COUNTRY = 'audience_country';
export const AUDIENCE_GENDER_AGE = 'audience_gender_age';
export const AUDIENCE_LOCALE = 'audience_locale';
export const EMAIL_CONTACTS = 'email_contacts';
export const FOLLOWER_COUNT = 'follower_count';
export const GET_DIRECTIONS_CLICKS = 'get_directions_clicks';
export const IMPRESSIONS = 'impressions';
export const ONLINE_FOLLOWERS = 'online_followers';
export const PHONE_CALL_CLICKS = 'phone_call_clicks';
export const PROFILE_VIEWS = 'profile_views';
export const REACH = 'reach';
export const TEXT_MESSAGE_CLICKS = 'text_message_clicks';
export const WEBSITE_CLICKS = 'website_clicks';

export const periods = [
  { label: 'Day', value: DAY },
  { label: 'Week', value: WEEK },
  { label: '28 Days', value: DAYS_28 },
  { label: 'Lifetime', value: LIFETIME },
];

export const datePresets = [
  {
    label: 'Last 30 Days',
    value: [moment().subtract(30, 'days').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')].join(
      ','
    ),
  },
  {
    label: 'This Month',
    value: [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')].join(
      ','
    ),
  },
  ...[...Array(23)]
    .map((_, index) => index + 1)
    .map((times) => ({
      label: moment().subtract(times, 'month').format('MMMM YYYY'),
      value: [
        moment().subtract(times, 'month').startOf('month').format('YYYY-MM-DD'),
        moment().subtract(times, 'month').endOf('month').format('YYYY-MM-DD'),
      ].join(','),
    })),
];

export const metrics = [
  {
    name: 'follower_count',
    title: 'Follower Count',
    value: FOLLOWER_COUNT,
    periods: [DAY],
    description:
      'Total number of new followers each day within the specified range. Returns a maximum of 30 days worth of data. Not available on IG Users with fewer than 100 followers.',
  },
  {
    name: 'profile_views',
    title: 'Profile Views',
    value: PROFILE_VIEWS,
    periods: [DAY],
    description:
      "Total number of users who have viewed the IG User's profile within the specified period.",
  },
  {
    name: 'website_clicks',
    title: 'Website Clicks',
    value: WEBSITE_CLICKS,
    periods: [DAY],
    description: "Total number of taps on the website link in the IG User's profile.",
  },
  {
    name: 'audience_city',
    title: 'Audience City',
    value: AUDIENCE_CITY,
    periods: [LIFETIME],
    description:
      "The cities of the IG User's followers. Does not include current day's data. Not available on IG Users with fewer than 100 followers.",
  },
  {
    name: 'audience_country',
    title: 'Audience Country',
    value: AUDIENCE_COUNTRY,
    periods: [LIFETIME],
    description:
      "The countries of the IG User's followers. Does not include current day's data. Not available on IG Users with fewer than 100 followers.",
  },
  {
    name: 'audience_gender_age',
    title: 'Audience Gender Age',
    value: AUDIENCE_GENDER_AGE,
    periods: [LIFETIME],
    description:
      "The gender and age distribution of the IG User's followers. Does not include current day's data. Not available on IG Users with fewer than 100 followers.",
  },
  {
    name: 'audience_locale',
    title: 'Audience Locale',
    value: AUDIENCE_LOCALE,
    periods: [LIFETIME],
    description:
      "The locales by country codes of the IG User's followers. Does not include current day's data. Not available on IG Users with fewer than 100 followers.",
  },
  {
    name: 'email_contacts',
    title: 'Email Contacts',
    value: EMAIL_CONTACTS,
    periods: [DAY],
    description: "Total number of taps on the email link in the IG User's profile.",
  },
  {
    name: 'get_directions_clicks',
    title: 'Get Directions Clicks',
    value: GET_DIRECTIONS_CLICKS,
    periods: [DAY],
    description: "Total number of taps on the directions link in the IG User's profile.",
  },
  {
    name: 'impressions',
    title: 'Impressions',
    value: IMPRESSIONS,
    periods: [DAY, WEEK, DAYS_28],
    description:
      "Total number of times the IG User's IG Media objects (i.e. posts, stories and promotions) have been viewed. Includes ad activity generated through the API, Facebook ads interfaces, and the Promote feature. Does not include profile views.",
  },
  {
    name: 'online_followers',
    title: 'Online Followers',
    value: ONLINE_FOLLOWERS,
    periods: [LIFETIME],
    description:
      "Total number of the IG User's followers who were online during the specified range. Not available on IG Users with fewer than 100 followers.",
  },
  {
    name: 'phone_call_clicks',
    title: 'Phone Call Clicks',
    value: PHONE_CALL_CLICKS,
    periods: [DAY],
    description: "Total number of taps on the call link in the IG User's profile.",
  },
  {
    name: 'reach',
    title: 'Reach',
    value: REACH,
    periods: [DAY, WEEK, DAYS_28],
    description:
      "Total number of unique users who have viewed at least one of the IG User's IG Media. Repeat views and views across different IG Media owned by the IG User by the same user are only counted as a single view. Includes ad activity generated through the API, Facebook ads interfaces, and the Promote feature.",
  },
  {
    name: 'text_message_clicks',
    title: 'Text Message Clicks',
    value: TEXT_MESSAGE_CLICKS,
    periods: [DAY],
    description: "Total number of taps on the text message link in the IG User's profile.",
  },
];
